import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { formatNumberToIndian, ChartAttributeName } from "utlis/utils";
import { capitalize } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const FONT_FAMILY = "Inter";
const FONT_SIZE = 16;
const FONT_WEIGHT = 600;
const COLORS = ["#7F56D9", "#B692F6", "#E9D7FE", "#F4EBFF"];

const getColor = (index) => COLORS[index % COLORS.length];

const BaseBarChart = ({
  labels,
  datasets,
  title,
  stacked = false,
  combineDatasets = false,
  hideLegend = false,
  showLabels = true, // New prop to control label visibility
  showBarContentBorders = false
}) => {
  const chartConfig = useMemo(() => {
    let chartDatasets = [];

    // Handling amounts and counts separately
    Object.keys(datasets).forEach((key, index) => {
      chartDatasets.push({
        label: `${ChartAttributeName(key)}`,
        backgroundColor: getColor(index),
        data: datasets[key].amounts.map((val) => (val === 0 ? null : val)), // Ignore zero values
        barPercentage: 1,
        categoryPercentage: 0.8,
        borderRadius: 3,
        borderWidth: 1,
        borderColor: "#ffffff",
        minBarLength: 5, // Ensure small non-zero bars are visible
      });

      chartDatasets.push({
        label: `${ChartAttributeName(key)} Counts`,
        backgroundColor: getColor(index + 1),
        data: datasets[key].counts.map((val) => (val === 0 ? null : val)), // Ignore zero values
        barPercentage: 1,
        categoryPercentage: 0.8,
        borderRadius: 3,
        borderWidth: 1,
        borderColor: "#ffffff",
        datalabels: {
          display: false, // Disable datalabels for count datasets
        },
        hidden: true,
        minBarLength: 5, // Ensure small non-zero bars are visible
      });
    });

    return {
      data: {
        labels: labels.map((label) => capitalize(label)),
        datasets: chartDatasets,
      },
      options: {
        plugins: {
          datalabels: {
            display: showLabels, // Use the new prop to control label visibility
            color: "#30301B",
            anchor: "end",
            align: "top",
            formatter: (value, context) => {
              // Add rupee symbol only for "Amounts" datasets and ignore zero values
              if (value && !context.dataset.label.includes("Counts")) {
                return value > 0 ? `₹${formatNumberToIndian(value)}` : "";
              }
              return "";
            },
            font: {
              family: FONT_FAMILY,
              weight: FONT_WEIGHT,
              size: 10,
            },
          },
          title: {
            display: true,
            text: title,
            align: "start",
            font: {
              family: FONT_FAMILY,
              weight: FONT_WEIGHT,
              size: FONT_SIZE,
            },
            padding: {
              top: 12,
              bottom: 24,
            },
            color: "#30301B",
          },
          legend: {
            display: hideLegend ? false : true,
            position: "bottom",
            align: "start",
            labels: {
              boxWidth: 6,
              boxHeight: 6,
              padding: 20,
              usePointStyle: true,
              pointStyle: "circle",
              font: {
                family: FONT_FAMILY,
                size: 12,
                weight: 400,
              },
              filter: (legendItem, chartData) => {
                // Filter out "Counts" datasets from the legend
                return !legendItem.text.includes("Counts");
              },
            },
          },
          tooltip: {
            enabled: true, // Enable tooltip
            callbacks: {
              title: function () {
                // Returning an empty array will hide the tooltip title
                return [];
              },
              label: function (tooltipItem) {
                const dataset = tooltipItem.dataset;
                const label = dataset.label || "";
                const index = tooltipItem.dataIndex;

                const countDatasetIndex =
                  tooltipItem.chart.data.datasets.findIndex(
                    (ds) =>
                      ds.label.includes("Counts") &&
                      ds.label.includes(label.split(" ")[0])
                  );
                const count =
                  countDatasetIndex !== -1
                    ? tooltipItem.chart.data.datasets[countDatasetIndex].data[
                        index
                      ]
                    : null;

                const formattedAmount = `₹${formatNumberToIndian(
                  tooltipItem.raw
                )}`;
                
                // Show both rupee symbol and count if available
                if (count && !showLabels) {
                  return ` ${formattedAmount} | Count: ${count}`;
                } else {
                  return formattedAmount;
                }
              },
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 3 / 1,
        scales: {
          x: {
            grid: {
              display: showBarContentBorders,
            },
            stacked: stacked,
            ticks: {
              font: {
                family: FONT_FAMILY,
              },
            },
          },
          y: {
            grid: {
              color: "#f3f5f7",
              borderWidth: 0.5,
            },
            stacked: stacked,
            ticks: {
              beginAtZero: true, // Ensure the chart starts at 0
              callback: formatNumberToIndian,
              font: {
                family: FONT_FAMILY,
              },
            },
          },
        },
      },
    };
  }, [labels, datasets, title, stacked, combineDatasets, showLabels]);

  return <Bar data={chartConfig.data} options={chartConfig.options} />;
};

export default BaseBarChart;
